import React, { useEffect, createContext, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { removeRefs } from '@/graphql/util';
import { Button } from '@material-ui/core';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import useQuery from '@/utilities/useQuery';
import { colors } from '@/styles';
import * as ROUTES from '@/constants/routes';
import { createOrderJob } from '../queries/createOrderJob';
import { useOrderErrors, useJobDetailsCallbacks } from '../hooks/jobDetails';
import { ServiceCatalogProvider } from '../hooks/serviceCatalog';
import { QUERY_JOB_BY_ID, FETCH_LOGOS } from '../graphql/queries';
import { DELETE_ORDER, CONFIRM_ORDERS, CLEAR_DUPLICATES, OVERRIDE_DUPLICATES } from '../graphql/mutations';
import { SHIPMENT_FORM_MODAL_PROGRESSION } from '../constants';
import { addDays } from 'date-fns';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { useClientUser } from '@/hooks';

const POLL_INVERVAL = 5 * 1000; //5s

const DAY = 1000 * 60 * 60 * 24;
const TODAY = new Date().setHours(0, 0, 0, 0);
const CUTOFF = new Date(TODAY - DAY * 365);

export const Context = createContext();

const JobDetailsContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { user_id, circles } = useClientUser();
    const { cache } = useApolloClient();

    const [progression, setProgression] = useState({
        current: 0,
        type: null,
    });

    const [expirationDate, setExpirationDate] = useState('');
    const [notification, setNotification] = useState({});
    const [deleteOrderModal, setDeleteOrderModal] = useState(null);

    const [isPolling, setPolling] = useState(false);
    const [editing, setEditing] = useState(null);
    const [editingPickup, setEditingPickup] = useState(null);
    const [disclaimer, setDisclaimer] = useState({});
    const [diagnostic, setDiagnostic] = useState({});

    const [fetchJob, { data: jobsData, startPolling, stopPolling, refetch }] = useLazyQuery(QUERY_JOB_BY_ID);

    const job = useMemo(() => {
        return jobsData?.result?.[0];
    }, [jobsData]);

    const [fetchLogos, { data: overrides }] = useLazyQuery(FETCH_LOGOS);
    const [deleteOrder, { loading: deleteOrderLoading }] = useMutation(DELETE_ORDER, {
        update: (cache, { data }) => {
            let next;
            cache.modify({
                id: cache.identify(job),
                fields: {
                    orders: (prev, { toReference }) => {
                        next = removeRefs(prev, data.removed.returning, { toReference });
                        return next;
                    },
                },
            });

            if (next.length === 0) {
                navigate(ROUTES.IMPORT_ORDERS);
            }
        },
        onCompleted: () => {
            setDeleteOrderModal(null);
        },
    });

    const [clearDuplicates] = useMutation(CLEAR_DUPLICATES, {
        update: (cache, { data }) => {
            cache.modify({
                id: cache.identify(job),
                fields: {
                    orders: (prev, { toReference }) => {
                        const next = removeRefs(prev, data.removed.returning, { toReference });

                        return next;
                    },
                },
            });
        },
    });

    const [overrideDuplicates] = useMutation(OVERRIDE_DUPLICATES, {
        update: (cache, { data }) => {
            cache.modify({
                id: cache.identify(job),
                fields: {
                    orders: (prev, { toReference }) => {
                        return prev.map((old) => {
                            const updates = data.updated.returning.find(
                                (updated) => toReference(updated).__ref === old.__ref
                            );

                            if (!!updates) {
                                return { ...old, ...updates };
                            }

                            return old;
                        });
                    },
                },
            });
        },
    });

    const [confirmOrders] = useMutation(CONFIRM_ORDERS, {
        onCompleted: (results) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'jobs' });
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });

            let message = `Successfully created ${
                results.confirmed_pending.affected_rows + results.confirmed_claimed.affected_rows
            } order(s)!`;
            // If any orders have been sent to the Onward Marketpalce, display a different snackbar success message instead.
            if (
                results?.confirmed_pending?.returning?.length &&
                results?.confirmed_pending?.returning.some((o) => {
                    return !o.oms && o.order_status === 'pending' && !o.carrier_id;
                })
            ) {
                message = "Check bid progress for your orders under 'Quote Summary.'";
            }

            setNotification({
                severity: 'success',
                message: message,
                customAction: (
                    <Button
                        variant="outlined"
                        onClick={() => navigate(ROUTES.IMPORT_ORDERS)}
                        css={css`
                            background: ${colors.greens.primary};
                            color: ${colors.white.primary};
                            border-color: #ffffff;
                        `}
                    >
                        Return
                    </Button>
                ),
            });
        },
    });

    const [submitOrders, { loading: submitting }] = useQuery(createOrderJob, {
        onComplete: () => {
            refetch();
        },
        onError: (err) => {
            refetch();
            Sentry.captureException(err);
        },
    });

    useEffect(() => {
        const orders = job?.orders || [];

        if (orders.length > 0) {
            fetchLogos({
                variables: {
                    shipper_id: job.client_id,
                    cutoff_ts: CUTOFF,
                    override_ids: [],
                },
            });
        }
    }, [job]);

    const orderTZ = useMemo(() => {
        return job?.orders?.[0]?.dropoff_zip
            ? zipcode_to_timezone.lookup(job.orders[0].dropoff_zip)
            : Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, [job]);

    useEffect(() => {
        const utc = addDays(new Date(asUTCDate(new Date().toISOString()).setUTCHours(0, 0, 0, 0)), 14).toISOString();
        setExpirationDate(asDateInTZ(utc, orderTZ).toISOString());
    }, [orderTZ]);

    useEffect(() => {
        if (job) {
            if (job.status === 'PROCESSING') {
                if (!isPolling) {
                    startPolling(POLL_INVERVAL);
                    setPolling(true);
                }
            } else {
                stopPolling();
                setPolling(false);
            }
        }
    }, [job, isPolling, startPolling, stopPolling]);

    const callbacks = useJobDetailsCallbacks(
        { progression, job, expirationDate },
        {
            setEditing,
            setEditingPickup,
            confirmOrders,
            deleteOrder,
            setProgression,
            submitOrders,
            overrideDuplicates,
            clearDuplicates,
        }
    );

    const hasErrors = useOrderErrors(job?.orders || []);

    return (
        <Context.Provider
            value={{
                state: {
                    diagnostic,
                    editing,
                    editingPickup,
                    current: (SHIPMENT_FORM_MODAL_PROGRESSION[progression.type] || [])[progression.current],
                    job,
                    isSubmitting: submitting,
                    expirationDate,
                    overrides: overrides?.results || [],
                    orderTZ,
                    notification,
                    clearNotification: () => {
                        setNotification({});
                    },
                    disclaimer,
                    deleteOrderModal,
                    deleteOrderLoading,
                },
                callbacks: {
                    ...callbacks,
                    fetchJob,
                    setExpirationDate,
                    setNotification,
                    setDisclaimer,
                    setDiagnostic,
                    clearNotification: () => {
                        setNotification({});
                    },
                    setDeleteOrderModal,
                },
                errors: {
                    hasErrors,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const ContextProvider = ({ children }) => (
    <ServiceCatalogProvider>
        <JobDetailsContextProvider>{children}</JobDetailsContextProvider>
    </ServiceCatalogProvider>
);
